import { apiRequest } from '../lib/api-client';

/** SMS on prepaid credit (context.md sections 72-74). */

export interface WalletView {
  rate_cents: number | null;
  balance_cents: number;
  reserved_cents: number;
  currency: string;
  low_balance_cents: number | null;
  is_low: boolean;
}

export function getWallet(): Promise<WalletView> {
  return apiRequest('/v1/sms/wallet');
}

export interface LedgerEntry {
  id: string;
  kind: 'CREDIT' | 'DEBIT' | 'RESERVATION' | 'RELEASE' | 'REFUND' | 'ADJUSTMENT';
  amount_cents: number;
  reserved_delta: number;
  balance_after: number;
  reserved_after: number;
  reason: string | null;
  actor_type: string;
  created_at: string;
}

export function listLedger(limit = 100): Promise<{ entries: LedgerEntry[] }> {
  return apiRequest(`/v1/sms/ledger?limit=${limit}`);
}

export interface Reconciliation {
  consistent: boolean;
  balanceDrift: number;
  reservedDrift: number;
}

export function reconcileWallet(): Promise<Reconciliation> {
  return apiRequest('/v1/sms/wallet/reconciliation');
}

export interface Estimate {
  recipients: number;
  priced: number;
  unpriced: string[];
  segments: number;
  total_price_cents: number;
  balance_cents: number;
  affordable: boolean;
  shortfall_cents: number;
}

export function estimateSend(input: { body: string; recipients: string[] }): Promise<Estimate> {
  return apiRequest('/v1/sms/estimate', { method: 'POST', body: input });
}

export interface SmsMessageView {
  id: string;
  recipient: string;
  body: string;
  segments: number;
  price_cents: number;
  status: string;
  failure_reason: string | null;
  queued_at: string;
  sent_at: string | null;
  delivered_at: string | null;
}

export interface SendResult {
  queued: SmsMessageView[];
  refused: { recipient: string; reason: string }[];
  reserved_cents: number;
  balance_cents: number;
}

export function sendSms(input: { body: string; recipients: string[] }): Promise<SendResult> {
  return apiRequest('/v1/sms/send', { method: 'POST', body: input });
}

export function listMessages(status?: string): Promise<{ messages: SmsMessageView[] }> {
  return apiRequest(`/v1/sms/messages${status ? `?status=${status}` : ''}`);
}

export interface UsageSummary {
  from: string;
  to: string;
  messages: number;
  segments: number;
  price_cents: number;
  delivered: number;
  failed: number;
  days: { day: string; status: string; messages: number; segments: number; price_cents: number }[];
}

export function getUsage(): Promise<UsageSummary> {
  return apiRequest('/v1/sms/usage');
}

export interface CampaignView {
  id: string;
  name: string;
  body: string;
  channel_id: string | null;
  audience_kind: 'LIST' | 'VIEW';
  audience: Record<string, unknown>;
  status: 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELED' | 'FAILED';
  scheduled_at: string | null;
  recipients_total: number;
  queued_count: number;
  refused_count: number;
  reserved_cents: number;
  failure_reason: string | null;
  finished_at: string | null;
  created_at: string;
}

export function listCampaigns(): Promise<{ campaigns: CampaignView[] }> {
  return apiRequest('/v1/sms/campaigns');
}

export function createCampaign(input: {
  name: string;
  body: string;
  recipients?: string[];
  viewId?: string;
}): Promise<CampaignView> {
  return apiRequest('/v1/sms/campaigns', { method: 'POST', body: input });
}

export interface CampaignEstimate extends Estimate {
  campaign_id: string;
}

export function estimateCampaign(id: string): Promise<CampaignEstimate> {
  return apiRequest(`/v1/sms/campaigns/${id}/estimate`);
}

export function scheduleCampaign(id: string, scheduledAt?: string): Promise<CampaignView> {
  return apiRequest(`/v1/sms/campaigns/${id}/schedule`, {
    method: 'POST',
    body: scheduledAt ? { scheduledAt } : {},
  });
}

export function cancelCampaign(id: string): Promise<CampaignView> {
  return apiRequest(`/v1/sms/campaigns/${id}/cancel`, { method: 'POST', body: {} });
}

// ---------------------------------------------------------------------------
// Buying credit
// ---------------------------------------------------------------------------

export interface TopUp {
  id: string;
  amountCents: number;
  currency: string;
  method: 'PIX' | 'CARD';
  status: 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED' | 'CANCELED';
  provider: string;
  pixPayload: string | null;
  pixQrBase64: string | null;
  checkoutUrl: string | null;
  expiresAt: string | null;
  paidAt: string | null;
  failureReason: string | null;
  createdAt: string;
}

export function listTopUps(): Promise<{
  topups: TopUp[];
  presets_cents: number[];
  provider: string;
}> {
  return apiRequest('/v1/sms/topups');
}

export function startTopUp(input: { amountCents: number; method: 'PIX' | 'CARD' }): Promise<TopUp> {
  return apiRequest('/v1/sms/topups', { method: 'POST', body: input });
}

export function getTopUp(id: string): Promise<TopUp> {
  return apiRequest(`/v1/sms/topups/${id}`);
}

export function simulateTopUp(id: string): Promise<TopUp> {
  return apiRequest(`/v1/sms/topups/${id}/simulate`, { method: 'POST' });
}
